import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface UnverifiedUser {
  auth_id: number;
  email: string;
  phone: string;
  created_at: string;
  role: string;
}

interface UnverifiedData {
  bandas: UnverifiedUser[];
  usuarios: UnverifiedUser[];
}

const ValidacoesPendentes: React.FC = () => {
  const [data, setData] = useState<UnverifiedData>({ bandas: [], usuarios: [] });
  const [expandedType, setExpandedType] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/unverified-users');
      setData(response.data);
    } catch (err) {
      setError('Erro ao carregar os dados.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleType = (type: string) => {
    setExpandedType(expandedType === type ? null : type);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copiado para a área de transferência!');
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Validações de Celular Pendentes</h1>
      {loading && <p style={styles.message}>Carregando...</p>}
      {error && <p style={{ ...styles.message, color: '#dc3545' }}>{error}</p>}

      {!loading && (
        <div>
          {/* Seção de Bandas */}
          <div style={styles.section}>
            <button
              style={{
                ...styles.typeButton,
                backgroundColor: expandedType === 'bandas' ? '#ff6600' : '#f2f2f2',
                color: expandedType === 'bandas' ? 'white' : '#333',
              }}
              onClick={() => toggleType('bandas')}
            >
              Bandas Pendentes ({data.bandas.length})
            </button>
            {expandedType === 'bandas' && (
              <div style={styles.list}>
                {data.bandas.map((banda) => (
                  <div key={banda.auth_id} style={styles.card}>
                    <span style={styles.timestamp}>
                      {formatDate(banda.created_at)}
                    </span>
                    <span style={styles.separator}>|</span>
                    <span 
                      style={styles.copyable}
                      onClick={() => copyToClipboard(banda.email)}
                    >
                      📧 {banda.email}
                    </span>
                    <span style={styles.separator}>|</span>
                    <span 
                      style={styles.copyable}
                      onClick={() => copyToClipboard(banda.phone)}
                    >
                      📱 {banda.phone}
                    </span>
                  </div>
                ))}
                {data.bandas.length === 0 && (
                  <p style={styles.message}>Nenhuma banda pendente de validação.</p>
                )}
              </div>
            )}
          </div>

          {/* Seção de Usuários */}
          <div style={styles.section}>
            <button
              style={{
                ...styles.typeButton,
                backgroundColor: expandedType === 'usuarios' ? '#ff6600' : '#f2f2f2',
                color: expandedType === 'usuarios' ? 'white' : '#333',
              }}
              onClick={() => toggleType('usuarios')}
            >
              Usuários Pendentes ({data.usuarios.length})
            </button>
            {expandedType === 'usuarios' && (
              <div style={styles.list}>
                {data.usuarios.map((usuario) => (
                  <div key={usuario.auth_id} style={styles.card}>
                    <span style={styles.timestamp}>
                      {formatDate(usuario.created_at)}
                    </span>
                    <span style={styles.separator}>|</span>
                    <span 
                      style={styles.copyable}
                      onClick={() => copyToClipboard(usuario.email)}
                    >
                      📧 {usuario.email}
                    </span>
                    <span style={styles.separator}>|</span>
                    <span 
                      style={styles.copyable}
                      onClick={() => copyToClipboard(usuario.phone)}
                    >
                      📱 {usuario.phone}
                    </span>
                  </div>
                ))}
                {data.usuarios.length === 0 && (
                  <p style={styles.message}>Nenhum usuário pendente de validação.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1000px',
    margin: '0 auto',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  title: {
    fontSize: '24px',
    color: '#333',
    textAlign: 'center' as 'center',
    marginBottom: '20px',
  },
  message: {
    fontSize: '16px',
    textAlign: 'center' as 'center',
    color: '#555',
  },
  section: {
    marginBottom: '20px',
  },
  typeButton: {
    width: '100%',
    padding: '10px',
    textAlign: 'left' as 'left',
    fontSize: '18px',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginBottom: '10px',
    transition: 'background-color 0.3s ease',
  },
  list: {
    paddingLeft: '10px',
  },
  card: {
    padding: '12px',
    marginBottom: '8px',
    backgroundColor: 'white',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
  },
  timestamp: {
    color: '#666',
    whiteSpace: 'nowrap' as 'nowrap',
  },
  separator: {
    color: '#ddd',
  },
  copyable: {
    cursor: 'pointer',
    color: '#0066cc',
    textDecoration: 'underline',
    ':hover': {
      color: '#0052a3',
    },
  },
};

export default ValidacoesPendentes;